'use client';

/**
 * HeadingField — Decorative heading / instruction text block.
 *
 * Config via field.metadata:
 *   level: 1 | 2 | 3 | 4 (default 3)
 *   align: 'left' | 'center' | 'right' (default 'left')
 *   description: string (rich body text below heading)
 */

import React from 'react';

import type { FieldComponentField } from '../formFieldTypes';

interface HeadingFieldProps {
  field: FieldComponentField;
  isDark: boolean;
}

export const HeadingField: React.FC<HeadingFieldProps> = ({ field, isDark }) => {
  const meta = field.metadata as {
    level?: number;
    align?: 'left' | 'center' | 'right';
    description?: string;
  } | undefined;
  const level: number = meta?.level || 3;
  const align = meta?.align || 'left';
  const description = meta?.description || field.help_text || '';
  const text = field.label || 'Heading';

  const textAlign =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  const sizeClass =
    level === 1
      ? 'text-2xl md:text-3xl'
      : level === 2
        ? 'text-xl md:text-2xl'
        : level === 4
          ? 'text-base'
          : 'text-lg md:text-xl'; // default h3

  const color = isDark ? 'text-gray-100' : 'text-gray-900';
  const descColor = isDark ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className={`col-span-full ${textAlign} py-1`}>
      <div className={`font-bold ${sizeClass} ${color}`}>{text}</div>
      {description && (
        <p className={`mt-1 text-sm leading-relaxed ${descColor}`}>{description}</p>
      )}
    </div>
  );
};
